/**
 * Merkzettel-I/O: PocketBase, N Reiter pro Sitz. Kein ARKO, kein Text in localStorage.
 * Klasse MERKZETTEL_SEAT_OR_USER.
 */

import { getPortalPocketBaseUrl, getPortalPbToken } from '../../constants';
import {
  MERKZETTEL_MAX_TABS,
  PORTAL_MERKZETTEL_COLLECTION,
  assertOwnMerkzettelRecords,
  buildMerkzettelPbFilter,
  canAddMerkzettelTab,
  clipMerkzettelBody,
  clipMerkzettelSort,
  clipMerkzettelTitle,
  emptyMerkzettelTab,
  ensureMerkzettelTabs,
  merkzettelFilterIsKanzleiWide,
  nextMerkzettelSort,
  planMerkzettelTabDelete,
  sanitizeMerkzettelSeatId,
  type MerkzettelRecord,
} from './portalMerkzettel';

type PbListResponse = {
  items?: Array<Record<string, unknown>>;
};

function pbBase(): string {
  return getPortalPocketBaseUrl().replace(/\/$/, '');
}

function authHeaders(): HeadersInit {
  const token = getPortalPbToken();
  if (!token) {
    throw new Error('Keine Session — bitte neu anmelden.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function mapRecord(raw: Record<string, unknown>): MerkzettelRecord | null {
  const id = typeof raw.id === 'string' ? raw.id : undefined;
  const customer_id = sanitizeMerkzettelSeatId(
    typeof raw.customer_id === 'string' ? raw.customer_id : '',
  );
  if (!customer_id) return null;
  return {
    id,
    customer_id,
    kanzlei_id:
      typeof raw.kanzlei_id === 'string' && raw.kanzlei_id.trim()
        ? sanitizeMerkzettelSeatId(raw.kanzlei_id)
        : undefined,
    title: clipMerkzettelTitle(typeof raw.title === 'string' ? raw.title : ''),
    body: clipMerkzettelBody(typeof raw.body === 'string' ? raw.body : ''),
    sort: clipMerkzettelSort(raw.sort),
  };
}

async function readError(res: Response): Promise<string> {
  let detail = `HTTP ${res.status}`;
  try {
    const data = (await res.json()) as { message?: unknown };
    if (typeof data.message === 'string' && data.message.trim()) {
      detail = data.message.trim().slice(0, 180);
    }
  } catch {
    /* ignore */
  }
  return detail;
}

function seatGuard(customerId: string): { cid: string; filter: string } {
  const cid = sanitizeMerkzettelSeatId(customerId);
  const filter = buildMerkzettelPbFilter(cid);
  if (!cid || !filter || merkzettelFilterIsKanzleiWide(filter)) {
    throw new Error('MERKZETTEL_SEAT_OR_USER: kein Sitz — Lesen abgebrochen');
  }
  return { cid, filter };
}

export async function loadMerkzettelTabsForSeat(customerId: string): Promise<MerkzettelRecord[]> {
  const { cid, filter } = seatGuard(customerId);
  const url =
    `${pbBase()}/api/collections/${PORTAL_MERKZETTEL_COLLECTION}/records` +
    `?filter=${encodeURIComponent(filter)}&sort=${encodeURIComponent('+sort,+id')}` +
    `&perPage=${MERKZETTEL_MAX_TABS + 5}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (res.status === 404) {
    throw new Error('Merkzettel-Speicher ist auf diesem System noch nicht eingerichtet.');
  }
  if (!res.ok) {
    throw new Error(`Merkzettel konnte nicht geladen werden (${await readError(res)}).`);
  }
  const data = (await res.json()) as PbListResponse;
  const mapped = (data.items || [])
    .map((row) => mapRecord(row))
    .filter((row): row is MerkzettelRecord => row != null);
  assertOwnMerkzettelRecords(mapped, cid);
  return ensureMerkzettelTabs(mapped, cid);
}

export async function saveMerkzettelTabForSeat(args: {
  customerId: string;
  kanzleiId?: string;
  recordId?: string;
  title: string;
  body: string;
  sort: number;
}): Promise<MerkzettelRecord> {
  const cid = sanitizeMerkzettelSeatId(args.customerId);
  if (!cid) {
    throw new Error('MERKZETTEL_SEAT_OR_USER: kein Sitz — Speichern abgebrochen');
  }
  const title = clipMerkzettelTitle(args.title);
  const body = clipMerkzettelBody(args.body);
  const sort = clipMerkzettelSort(args.sort);
  const payload: Record<string, string | number> = {
    customer_id: cid,
    title,
    body,
    sort,
  };
  const kid = sanitizeMerkzettelSeatId(args.kanzleiId);
  if (kid) payload.kanzlei_id = kid;

  if (args.recordId) {
    const res = await fetch(
      `${pbBase()}/api/collections/${PORTAL_MERKZETTEL_COLLECTION}/records/${encodeURIComponent(args.recordId)}`,
      {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ title, body, sort, customer_id: cid }),
      },
    );
    if (!res.ok) {
      throw new Error(`Merkzettel konnte nicht gespeichert werden (${await readError(res)}).`);
    }
    const updated = (await res.json()) as Record<string, unknown>;
    const mapped = mapRecord(updated);
    if (!mapped || mapped.customer_id !== cid) {
      throw new Error('MERKZETTEL_SEAT_OR_USER: Speichern ohne eigenen Sitz verworfen');
    }
    return mapped;
  }

  const existing = await loadMerkzettelTabsForSeat(cid);
  const persisted = existing.filter((row) => row.id);
  if (!canAddMerkzettelTab(persisted) && persisted.length >= MERKZETTEL_MAX_TABS) {
    throw new Error(`Höchstens ${MERKZETTEL_MAX_TABS} Reiter pro Sitz.`);
  }

  const res = await fetch(`${pbBase()}/api/collections/${PORTAL_MERKZETTEL_COLLECTION}/records`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      ...payload,
      sort: persisted.length ? nextMerkzettelSort(persisted) : sort,
    }),
  });
  if (res.status === 404) {
    throw new Error('Merkzettel-Speicher ist auf diesem System noch nicht eingerichtet.');
  }
  if (!res.ok) {
    throw new Error(`Merkzettel konnte nicht gespeichert werden (${await readError(res)}).`);
  }
  const created = (await res.json()) as Record<string, unknown>;
  const mapped = mapRecord(created);
  if (!mapped?.id || mapped.customer_id !== cid) {
    throw new Error('MERKZETTEL_SEAT_OR_USER: Anlage ohne eigenen Sitz verworfen');
  }
  return mapped;
}

export async function createMerkzettelTabForSeat(args: {
  customerId: string;
  kanzleiId?: string;
}): Promise<MerkzettelRecord> {
  let tabs = await loadMerkzettelTabsForSeat(args.customerId);
  if (tabs.length === 1 && !tabs[0].id) {
    const first = await saveMerkzettelTabForSeat({
      customerId: args.customerId,
      kanzleiId: args.kanzleiId,
      title: tabs[0].title,
      body: tabs[0].body,
      sort: tabs[0].sort,
    });
    tabs = [first];
  }
  const persisted = tabs.filter((row) => row.id);
  if (!canAddMerkzettelTab(tabs) || persisted.length >= MERKZETTEL_MAX_TABS) {
    throw new Error(`Höchstens ${MERKZETTEL_MAX_TABS} Reiter pro Sitz.`);
  }
  const draft = emptyMerkzettelTab(args.customerId, nextMerkzettelSort(tabs));
  return saveMerkzettelTabForSeat({
    customerId: args.customerId,
    kanzleiId: args.kanzleiId,
    title: draft.title,
    body: draft.body,
    sort: draft.sort,
  });
}

export async function removeMerkzettelTabForSeat(args: {
  customerId: string;
  recordId?: string;
}): Promise<{ mode: 'deleted' | 'cleared'; tabs: MerkzettelRecord[] }> {
  const cid = sanitizeMerkzettelSeatId(args.customerId);
  if (!cid) {
    throw new Error('MERKZETTEL_SEAT_OR_USER: kein Sitz — Löschen abgebrochen');
  }
  const tabs = await loadMerkzettelTabsForSeat(cid);
  const mode = planMerkzettelTabDelete(tabs.filter((row) => row.id).length || tabs.length);
  const target = args.recordId ? tabs.find((row) => row.id === args.recordId) : tabs[0];
  if (!target || target.customer_id !== cid) {
    throw new Error('MERKZETTEL_SEAT_OR_USER: fremde Notiz verworfen');
  }

  if (mode === 'clear-last') {
    if (!target.id) {
      return { mode: 'cleared', tabs: [emptyMerkzettelTab(cid, 0)] };
    }
    const cleared = await saveMerkzettelTabForSeat({
      customerId: cid,
      recordId: target.id,
      title: '',
      body: '',
      sort: clipMerkzettelSort(target.sort),
    });
    return { mode: 'cleared', tabs: [cleared] };
  }

  if (!target.id) {
    return { mode: 'deleted', tabs: ensureMerkzettelTabs(tabs.filter((row) => row !== target), cid) };
  }

  const res = await fetch(
    `${pbBase()}/api/collections/${PORTAL_MERKZETTEL_COLLECTION}/records/${encodeURIComponent(target.id)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(`Reiter konnte nicht gelöscht werden (${await readError(res)}).`);
  }
  const next = await loadMerkzettelTabsForSeat(cid);
  return { mode: 'deleted', tabs: next };
}
